import React, { createContext, useCallback, useContext, useMemo } from "react";
import Swal from "sweetalert2";

const ConfirmContext = createContext(null);

const buildDialogConfig = (input) => {
  if (typeof input === "string") {
    return {
      title: "Please confirm",
      message: input,
      confirmText: "Confirm",
      cancelText: "Cancel",
      tone: "default",
    };
  }

  return {
    title: "Please confirm",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    tone: "default",
    ...input,
  };
};

const iconByTone = {
  default: "question",
  warning: "warning",
  danger: "warning",
};

const confirmColorByTone = {
  default: "#2563EB",
  warning: "#D97706",
  danger: "#DC2626",
};

export const ConfirmProvider = ({ children }) => {
  const confirm = useCallback(async (input) => {
    const dialog = buildDialogConfig(input);
    const result = await Swal.fire({
      title: dialog.title,
      text: dialog.message,
      icon: iconByTone[dialog.tone] || "question",
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      confirmButtonText: dialog.confirmText,
      cancelButtonText: dialog.cancelText,
      confirmButtonColor:
        dialog.confirmButtonColor || confirmColorByTone[dialog.tone] || "#2563EB",
      cancelButtonColor: dialog.cancelButtonColor || "#64748B",
      allowOutsideClick: dialog.allowOutsideClick ?? true,
      allowEscapeKey: dialog.allowEscapeKey ?? true,
    });

    return result.isConfirmed;
  }, []);

  const value = useMemo(
    () => ({
      confirm,
    }),
    [confirm],
  );

  return <ConfirmContext.Provider value={value}>{children}</ConfirmContext.Provider>;
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within ConfirmProvider.");
  }
  return context;
};
